/* import __COLOCATED_TEMPLATE__ from './application-view.hbs'; */
import Component from '@ember/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { modifier } from 'ember-modifier';
import ENV from '../config/environment';
import Constants from '../utils/constants';
import { tracked } from '@glimmer/tracking';

let delay = function (t, v) {
    return new Promise(function (resolve) {
        setTimeout(resolve.bind(null, v), t);
    });
};

const widthTracker = modifier(
    (element, [getDimensions]) => {
        let resizer = () => {
            const rect = element.getBoundingClientRect();
            getDimensions(rect.width, rect.height);
        };
        resizer(); // On first priming, make sure it is off the item
        window.addEventListener('resize', resizer);
        return () => {
            window.removeEventListener('resize', resizer);
        };
    },
    { eager: false },
);

export default class ApplicationView extends Component {
    tagName = '';

    @service('aditools-analytics') analytics;
    @service('aditools-site') site;
    @service selectionData;
    @service intl;
    @service urlData;
    @service hashService;
    @service tabService;
    @service('data') model;
    @service componentManager;

    trackSize = widthTracker;
    @tracked appWidth;
    @tracked generalDialog = null;
    @tracked adcDeviceTable = null;
    @tracked driverDeviceTable = null;
    @tracked systemDialog = null;
    @tracked deviceTableName = null;

    init() {
        super.init();
        // TODO: Can we find a better solution than invoking the runloop's scheduleOnce?
        // Calling this._loadTool(); syncronously from here doesn't work
        scheduleOnce('afterRender', this, '_loadTool');
    }

    _loadTool() {
        if (this.hashService.loadedWithHash) {
            this.componentManager.initialLoadWithHash();
            this.analytics.setWebEvent(ENV.toolname, 'hashAtStart', this.hashService.locationHash, true);
        } else {
            this.componentManager.initialLoadWithoutHash();
        }

        this.tabService.updateTabs(this.model);
        this.hashService.handleUrlParameters();

        this.generalDialog = { title: '', message: '', enabled: false };
        this.adcDeviceTable = { title: '', enabled: false };
        this.driverDeviceTable = { title: '', enabled: false };
        this.systemDialog = { message: '', enabled: false };
    }

    ////////////////////////////////////////////////////////////////////////////////
    // Controller Actions                                                         //
    ////////////////////////////////////////////////////////////////////////////////
    @action setAppWidth(width) {
        this.appWidth = width;
    }

    @action updateAppData() {
        this.tabService.updateTabs(this.model);
        this.hashService.handleUrlParameters();
    }

    @action giveFeedback(feedbackModel) {
        let feedbackData = {
            ToolId: Constants.API_TOOL_ID,
            Name: encodeURIComponent(feedbackModel.name),
            Email: encodeURIComponent(feedbackModel.email),
            Feedback: encodeURIComponent(feedbackModel.feedback),
        };

        return Promise.resolve()
            .then(() => {
                this._systemDialogFunc('ON', 'applicationBusy');
                return delay(250).then(() => {
                    return;
                });
            })
            .then(() => {
                return fetch(this.urlData.feedbackUrl, {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    method: 'POST',
                    body: JSON.stringify(feedbackData),
                })
                    .then((response) => {
                        return response.json();
                    })
                    .catch(() => {
                        return { status: -1 };
                    });
            })
            .then((result) => {
                this._systemDialogFunc('OFF', '');
                if (result.status === 0) {
                    this.openDialog('icon_modal.feedback.title', 'icon_modal.feedback.feedbackDialogOkMessage');
                } else {
                    this.openDialog('icon_modal.feedback.title', 'icon_modal.feedback.feedbackDialogNgMessage');
                }
            })
            .catch(() => {
                this._systemDialogFunc('OFF', '');
                this.openDialog('icon_modal.feedback.title', 'icon_modal.feedback.feedbackDialogNgMessage');
            });
    }

    @action openDialog(title, message, msgparams) {
        let adjparams = msgparams;
        if (adjparams) {
            adjparams.htmlSafe = 'true';
        } else {
            adjparams = { htmlSafe: 'true' };
        }
        this.generalDialog = {
            title: this.intl.t(title, { htmlSafe: 'true' }),
            message: this.intl.t(message, adjparams),
            enabled: true,
        };

        this.analytics.setWebEvent(ENV.toolname, 'Help Dialog', this.generalDialog.title.toString());
    }

    @action closeDialog() {
        this.generalDialog = { enabled: false, title: '', message: '' };
    }

    @action openDeviceTable(name) {
        this.deviceTableName = name;
        if (name === 'adc') {
            this.adcDeviceTable = { enabled: true, title: this.intl.t('adc_modal.title') };
        } else {
            this.driverDeviceTable = { enabled: true, title: this.intl.t('driver_modal.title') };
        }

        this.analytics.setWebEvent(
            ENV.toolname,
            this.deviceTableName === 'adc'
                ? `${this.adcDeviceTable.title.toString()}`
                : `${this.driverDeviceTable.title.toString()}`,
            this.deviceTableName === 'adc'
                ? this.selectionData.selectedAdc.part
                : this.selectionData.selectedDriver.part,
        );
    }

    @action closeAdcDeviceTable() {
        if (this.deviceTableName === 'adc') {
            this.adcDeviceTable = { enabled: false, title: '' };
        } else {
            this.driverDeviceTable = { enabled: false, title: '' };
        }

        this.analytics.setWebEvent(
            ENV.toolname,
            this.deviceTableName === 'adc' ? `Adc Device Table Closed` : `Driver Device Table Closed`,
            this.deviceTableName === 'adc'
                ? this.selectionData.selectedAdc.part
                : this.selectionData.selectedDriver.part,
        );
    }

    ////////////////////////////////////////////////////////////////////////////////
    // Internal Support Functions                                                 //
    ////////////////////////////////////////////////////////////////////////////////
    _systemDialogFunc(status, message) {
        switch (status) {
            case 'ON':
                this.systemDialog = { enabled: true, message: this.intl.t(`icon_modal.system.${message}`) };
                break;
            case 'OFF':
                this.systemDialog = { enabled: false, message: '' };
                break;
            case 'NONE':
                break;
            default:
                break;
        }
    }
}
